import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import {
  Card,
  CardContent,
} from "../components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  MessageSquare,
  User,
  Loader2,
  ArrowLeft,
  UserPlus,
  Share2,
  Bookmark,
  Copy,
  QrCode,
  Users,
  Store,
  UtensilsCrossed,
  Check,
} from "lucide-react";
import QRCode from "qrcode";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('session_token');
  if (token) {
    return { 'Authorization': `Bearer ${token}` };
  }
  return {};
};

const PublicProfile = () => {
  const { nickname } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [startingChat, setStartingChat] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState(null);
  const [showSaveMenu, setShowSaveMenu] = useState(false);
  const [savingContact, setSavingContact] = useState(false);
  const [savedSuccess, setSavedSuccess] = useState(false);

  const profileUrl = `${window.location.origin}/u/${nickname}`;

  useEffect(() => {
    fetchPublicProfile();
    generateQRCode();
  }, [nickname]);

  const generateQRCode = async () => {
    try {
      const url = await QRCode.toDataURL(profileUrl, {
        width: 250,
        margin: 2,
        color: { dark: '#000000', light: '#ffffff' }
      });
      setQrCodeUrl(url);
    } catch (err) {
      console.error("Error generating QR:", err);
    }
  };

  const fetchPublicProfile = async () => {
    try {
      const response = await fetch(`${API}/u/${nickname}`);
      
      if (response.ok) {
        const data = await response.json();
        setProfile(data);
      } else if (response.status === 404) {
        setError("Usuario no encontrado");
      } else if (response.status === 403) {
        setError("Este perfil no está disponible");
      } else {
        setError("Error al cargar el perfil");
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
      setError("Error de conexión");
    } finally {
      setLoading(false);
    }
  };

  const handleStartChat = async () => {
    if (!user) {
      toast.error("Debes iniciar sesión para chatear");
      navigate("/");
      return;
    }
    
    if (user.user_id === profile?.user_id) {
      toast.error("No puedes chatear contigo mismo");
      return;
    }
    
    setStartingChat(true);
    try {
      const headers = { "Content-Type": "application/json", ...getAuthHeaders() };
      
      const response = await fetch(`${API}/conversations`, {
        method: "POST",
        headers: headers,
        credentials: "include",
        body: JSON.stringify({
          participant_ids: [profile.user_id],
          is_group: false,
        }),
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success("Chat iniciado");
        navigate(`/chat/${data.conversation_id}`);
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.detail || "Error al iniciar chat");
      }
    } catch (error) {
      console.error("Chat error:", error);
      toast.error("Error de conexión");
    } finally {
      setStartingChat(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(profileUrl);
      toast.success("Link copiado al portapapeles");
    } catch (err) {
      toast.error("Error al copiar link");
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${profile?.name} en OkChat`,
          text: `Chatea con ${profile?.name} en OkChat`,
          url: profileUrl,
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          handleCopyLink();
        }
      }
    } else {
      handleCopyLink();
    }
  };

  const handleSaveToCategory = async (category) => {
    if (!user) {
      toast.error("Debes iniciar sesión");
      return;
    }
    
    setSavingContact(true);
    try {
      const response = await fetch(`${API}/user/saved/${category}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        credentials: "include",
        body: JSON.stringify({ category, user_id: profile.user_id })
      });
      
      if (response.ok) {
        const categoryNames = {
          contacts: "Contactos",
          stores: "Mis Tiendas",
          restaurants: "Mis Restaurantes"
        };
        toast.success(`Guardado en ${categoryNames[category]}`);
        setSavedSuccess(true);
        setShowSaveMenu(false);
      } else {
        toast.error("Error al guardar");
      }
    } catch (err) {
      toast.error("Error al guardar");
    }
    setSavingContact(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0f0f17] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-red-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0f0f17] flex flex-col items-center justify-center text-white p-4">
        <div className="text-center">
          <User className="w-16 h-16 text-slate-600 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">{error}</h1>
          <p className="text-slate-400 mb-6">
            El perfil que buscas no existe o no está disponible
          </p>
          <Button onClick={() => navigate("/")} className="bg-red-600 hover:bg-red-700">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Ir al inicio
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0f0f17] text-white">
      {/* Header */}
      <header className="border-b border-slate-800 bg-[#1a1a24]">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="text-slate-400 hover:text-white"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-red-500" />
            <span className="font-semibold">OkChat</span>
          </div>
          <div className="w-10" />
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        <Card className="bg-[#1a1a24] border-slate-800">
          <CardContent className="p-8">
            {/* Profile Header */}
            <div className="flex flex-col items-center text-center">
              {/* Avatar with status */}
              <div className="relative mb-4">
                <Avatar className="w-32 h-32">
                  <AvatarImage src={profile?.picture ? `${BACKEND_URL}${profile.picture}` : null} />
                  <AvatarFallback className="bg-gradient-to-br from-red-500 to-indigo-600 text-white text-4xl">
                    {profile?.name?.charAt(0)?.toUpperCase() || "U"}
                  </AvatarFallback>
                </Avatar>
                {profile?.is_online && (
                  <div className="absolute bottom-2 right-2 w-6 h-6 bg-green-500 rounded-full border-4 border-[#1a1a24]" />
                )}
              </div>
              
              {/* Name & Nickname */}
              <h1 className="text-3xl font-bold mb-1">{profile?.name}</h1>
              <p className="text-lg text-slate-400 mb-4">@{profile?.nickname}</p>
              
              {/* Status Badge */}
              <div className={`px-4 py-1.5 rounded-full text-sm font-medium mb-6 ${
                profile?.is_online 
                  ? 'bg-green-500/20 text-green-400' 
                  : 'bg-slate-700/50 text-slate-400'
              }`}>
                {profile?.is_online ? '● En línea' : '○ Desconectado'}
              </div>
              
              {/* Stats */}
              <div className="flex gap-8 mb-8">
                <div className="text-center">
                  <p className="text-2xl font-bold text-red-400">{profile?.message_count || 0}</p>
                  <p className="text-sm text-slate-400">Mensajes</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-slate-400">
                    {profile?.created_at 
                      ? new Date(profile.created_at).toLocaleDateString('es-MX', { month: 'short', year: 'numeric' })
                      : '-'}
                  </p>
                  <p className="text-sm text-slate-400">Miembro desde</p>
                </div>
              </div>
              
              {/* Action Buttons */}
              {user && user.user_id !== profile?.user_id ? (
                <div className="w-full max-w-xs space-y-3">
                  {/* Chatear Button */}
                  <Button
                    onClick={handleStartChat}
                    disabled={startingChat}
                    size="lg"
                    className="bg-red-600 hover:bg-red-700 w-full"
                  >
                    {startingChat ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Iniciando...
                      </>
                    ) : (
                      <>
                        <MessageSquare className="w-5 h-5 mr-2" />
                        Chatear
                      </>
                    )}
                  </Button>

                  {/* Share & Save Buttons */}
                  <div className="flex gap-2">
                    <Button
                      onClick={handleShare}
                      variant="outline"
                      size="lg"
                      className="flex-1 border-slate-700 text-slate-300 hover:bg-[#232e3c] hover:text-white"
                    >
                      <Share2 className="w-4 h-4 mr-2" />
                      Compartir
                    </Button>

                    <DropdownMenu open={showSaveMenu} onOpenChange={setShowSaveMenu}>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="outline"
                          size="lg"
                          className={`flex-1 border-slate-700 hover:bg-[#232e3c] ${
                            savedSuccess ? 'text-green-400 border-green-600' : 'text-slate-300 hover:text-white'
                          }`}
                        >
                          {savedSuccess ? (
                            <>
                              <Check className="w-4 h-4 mr-2" />
                              Guardado
                            </>
                          ) : (
                            <>
                              <Bookmark className="w-4 h-4 mr-2" />
                              Guardar
                            </>
                          )}
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="bg-[#232e3c] border-[#0e1621]">
                        <DropdownMenuItem 
                          onClick={() => handleSaveToCategory("contacts")}
                          className="text-white hover:bg-[#2b5278] cursor-pointer"
                          disabled={savingContact}
                        >
                          <Users className="w-4 h-4 mr-2 text-blue-400" />
                          Contactos
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => handleSaveToCategory("stores")}
                          className="text-white hover:bg-[#2b5278] cursor-pointer"
                          disabled={savingContact}
                        >
                          <Store className="w-4 h-4 mr-2 text-orange-400" />
                          Mis Tiendas
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => handleSaveToCategory("restaurants")}
                          className="text-white hover:bg-[#2b5278] cursor-pointer"
                          disabled={savingContact}
                        >
                          <UtensilsCrossed className="w-4 h-4 mr-2 text-green-400" />
                          Mis Restaurantes
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ) : !user ? (
                <div className="space-y-4 w-full max-w-xs">
                  <Button
                    onClick={() => navigate("/")}
                    size="lg"
                    className="bg-red-600 hover:bg-red-700 w-full"
                  >
                    <UserPlus className="w-5 h-5 mr-2" />
                    Crear cuenta para chatear
                  </Button>
                  <p className="text-sm text-slate-400">
                    Inicia sesión o crea una cuenta para enviar mensajes
                  </p>
                </div>
              ) : (
                <p className="text-slate-400">Este es tu perfil público</p>
              )}

              {/* QR Code & Link Section */}
              <div className="mt-8 pt-8 border-t border-slate-700 w-full">
                <p className="text-sm text-slate-400 mb-4 flex items-center justify-center">
                  <Share2 className="w-4 h-4 mr-2" />
                  Compartir perfil
                </p>
                
                {/* QR Code - Bigger */}
                {qrCodeUrl && (
                  <div className="flex justify-center mb-4">
                    <div className="bg-white p-4 rounded-xl shadow-lg">
                      <img src={qrCodeUrl} alt="QR Code" className="w-48 h-48" />
                    </div>
                  </div>
                )}
                
                {/* Profile Link */}
                <div className="flex items-center gap-2 justify-center bg-slate-800 px-4 py-3 rounded-lg max-w-xs mx-auto">
                  <code className="text-sm text-slate-300 truncate flex-1">
                    {profileUrl}
                  </code>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleCopyLink}
                    className="text-slate-400 hover:text-white shrink-0"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* OkChat Branding */}
        <div className="text-center mt-8">
          <p className="text-sm text-slate-500">
            Perfil de OkChat • 
            <a href="/" className="text-red-400 hover:text-red-300 ml-1">
              Únete a la conversación
            </a>
          </p>
        </div>
      </main>
    </div>
  );
};

export default PublicProfile;
